import { ResizeObserverBoxOptions } from '../ResizeObserverBoxOptions';
import { DOMRectReadOnly } from '../DOMRectReadOnly';
import { isSVG, isHidden } from '../utils/element';
const cache = new Map();
const scrollRegexp = /auto|scroll/;
const IE = (/msie|trident/i).test(navigator.userAgent);
const parseDimension = (pixel) => parseFloat(pixel || '0');
const size = (inlineSize = 0, blockSize = 0) => {
    return Object.freeze({ inlineSize, blockSize });
};
const zeroBoxes = Object.freeze({
    borderBoxSize: size(),
    contentBoxSize: size(),
    scrollBoxSize: size(),
    devicePixelBorderBoxSize: size(),
    contentRect: new DOMRectReadOnly(0, 0, 0, 0)
});
const calculateBoxSizes = (target) => {
    if (cache.has(target)) {
        return cache.get(target);
    }
    if (isHidden(target)) {
        cache.set(target, zeroBoxes);
        return zeroBoxes;
    }
    const cs = getComputedStyle(target);
    const dpr = window.devicePixelRatio;
    const svg = isSVG(target) && target.getBBox();
    const removePadding = !IE && cs.boxSizing === 'border-box';
    const canScrollVertically = !svg && scrollRegexp.test(cs.overflowY || '');
    const canScrollHorizontally = !svg && scrollRegexp.test(cs.overflowX || '');
    const paddingTop = svg ? 0 : parseDimension(cs.paddingTop);
    const paddingRight = svg ? 0 : parseDimension(cs.paddingRight);
    const paddingBottom = svg ? 0 : parseDimension(cs.paddingBottom);
    const paddingLeft = svg ? 0 : parseDimension(cs.paddingLeft);
    const borderTop = svg ? 0 : parseDimension(cs.borderTopWidth);
    const borderRight = svg ? 0 : parseDimension(cs.borderRightWidth);
    const borderBottom = svg ? 0 : parseDimension(cs.borderBottomWidth);
    const borderLeft = svg ? 0 : parseDimension(cs.borderLeftWidth);
    const horizontalPadding = paddingLeft + paddingRight;
    const verticalPadding = paddingTop + paddingBottom;
    const horizontalBorderArea = borderLeft + borderRight;
    const verticalBorderArea = borderTop + borderBottom;
    const horizontalScrollbarThickness = !canScrollHorizontally ? 0 : target.offsetHeight - verticalBorderArea - target.clientHeight;
    const verticalScrollbarThickness = !canScrollVertically ? 0 : target.offsetWidth - horizontalBorderArea - target.clientWidth;
    const widthReduction = removePadding ? horizontalPadding + horizontalBorderArea : 0;
    const heightReduction = removePadding ? verticalPadding + verticalBorderArea : 0;
    const contentWidth = svg ? svg.width : parseDimension(cs.width) - widthReduction - verticalScrollbarThickness;
    const contentHeight = svg ? svg.height : parseDimension(cs.height) - heightReduction - horizontalScrollbarThickness;
    const borderBoxWidth = contentWidth + horizontalPadding + verticalScrollbarThickness + horizontalBorderArea;
    const borderBoxHeight = contentHeight + verticalPadding + horizontalScrollbarThickness + verticalBorderArea;
    const boxes = Object.freeze({
        borderBoxSize: size(borderBoxWidth, borderBoxHeight),
        contentBoxSize: size(contentWidth, contentHeight),
        scrollBoxSize: size(contentWidth + horizontalPadding, contentHeight + verticalPadding),
        devicePixelBorderBoxSize: size(borderBoxWidth * dpr, borderBoxHeight * dpr),
        contentRect: new DOMRectReadOnly(paddingLeft, paddingTop, contentWidth, contentHeight)
    });
    cache.set(target, boxes);
    return boxes;
};
const calculateBoxSize = (target, observedBox) => {
    const boxes = calculateBoxSizes(target);
    switch (observedBox) {
        case ResizeObserverBoxOptions.BORDER_BOX:
            return boxes.borderBoxSize;
        case ResizeObserverBoxOptions.SCROLL_BOX:
            return boxes.scrollBoxSize;
        case ResizeObserverBoxOptions.DEVICE_PIXEL_BORDER_BOX:
            return boxes.devicePixelBorderBoxSize;
        case ResizeObserverBoxOptions.CONTENT_BOX:
        default:
            return boxes.contentBoxSize;
    }
};
export { calculateBoxSize, calculateBoxSizes, cache };
